import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { ArrowRight } from 'lucide-react'
import { SEO } from '@/components/common/SEO'
import { ListingGrid } from '@/components/listings'
import { AdCarousel } from '@/components/ads/AdCarousel'
import { useRecentListings } from '@/hooks'
import { STORE } from '@/config/store'
import { CATEGORY_GROUPS } from '@/utils/categories'
import { CategoryGroupIcon } from '@/components/icons/CategoryGroupIcons'

export function HomePage() {
  // Single-store build: scope to this shop's seller. Unscoped, this subscribes
  // to every active listing across the whole iSQROLL marketplace (~1,800 docs
  // in one realtime listen) and renders other sellers' stock on our shopfront.
  const { listings: recentListings, isLoading, error } = useRecentListings(8, {
    sellerId: STORE.sellerId,
  })

  return (
    <div className="pb-20 md:pb-0">
      <SEO
        title="Home"
        description="New Zealand's online marketplace. Buy and sell vehicles, goods, and fundraisers on iSQROLL."
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "iSQROLL",
          "url": "https://isqroll.co.nz",
          "logo": "https://isqroll.co.nz/images/Isqroll-default-logo.png",
          "description": "New Zealand's online marketplace. Buy and sell vehicles, goods, and fundraisers.",
          "sameAs": []
        })}</script>
      </Helmet>

      {/* Hero + compact categories */}
      <section className="py-10 md:py-14">
        <div className="container">
          <div className="grid lg:grid-cols-[1.1fr_0.8fr] items-center gap-4 lg:gap-0">
            {/* Left: title, description, compact categories */}
            <div>
              <h1
                className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-primary-600 opacity-0 animate-fade-in-up"
                style={{ animationDelay: '0s' }}
              >
                New Zealand&apos;s Marketplace
              </h1>
              <p
                className="text-lg md:text-xl text-slate-600 mb-8 max-w-xl opacity-0 animate-fade-in-up"
                style={{ animationDelay: '0.1s' }}
              >
                Buy and sell everything from vehicles to goods and fundraisers.
                Join thousands of Kiwis trading every day.
              </p>

              {/* Compact browse categories (replaces hero buttons) */}
              <div
                className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 max-w-xl opacity-0 animate-fade-in-up"
                style={{ animationDelay: '0.2s' }}
              >
                {CATEGORY_GROUPS.map((group) => (
                  <Link
                    key={group.id}
                    to={`/browse?group=${group.id}`}
                    className="card p-3 sm:p-4 text-center hover:scale-105 hover:border-primary-300 hover:shadow-md transition-all duration-200"
                  >
                    <div className="flex justify-center mb-2">
                      <CategoryGroupIcon iconKey={group.icon} className="w-10 h-10" />
                    </div>
                    <span className="text-xs sm:text-sm font-semibold text-slate-700 leading-tight">{group.name}</span>
                  </Link>
                ))}
              </div>
            </div>

            {/* Right: full Tui mascot */}
            <div className="hidden lg:flex justify-start">
              <img
                src="/images/tuatara-mascot.webp"
                alt="iSQROLL Tui mascot"
                className="w-full max-w-xs lg:max-w-sm opacity-0 animate-fade-in-up"
                style={{ animationDelay: '0.15s' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Sponsor Ads */}
      <section className="py-6 md:py-8">
        <div className="container max-w-4xl">
          <AdCarousel placement="homepage-banner" />
        </div>
      </section>

      {/* Recent Listings */}
      <section className="py-12 md:py-16 bg-slate-50">
        <div className="container">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl md:text-3xl font-bold">Recent Listings</h2>
            <Link
              to="/browse"
              className="flex items-center gap-1 text-primary-600 hover:text-primary-700 font-medium"
            >
              View all
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {error ? (
            <div className="text-center py-12 bg-white rounded-xl">
              <h3 className="text-lg font-semibold mb-2">We couldn&apos;t load our products</h3>
              <p className="text-slate-500">{error}</p>
            </div>
          ) : recentListings.length > 0 || isLoading ? (
            <ListingGrid
              listings={recentListings}
              isLoading={isLoading}
              skeletonCount={8}
              emptyMessage="No products listed yet — check back soon."
            />
          ) : (
            <div className="text-center py-12 bg-white rounded-xl">
              <h3 className="text-lg font-semibold mb-2">No products listed yet</h3>
              <p className="text-slate-500">Check back soon — we add new stock regularly.</p>
            </div>
          )}
        </div>
      </section>

      {/* Sponsor Ads - secondary placement */}
      <section className="py-6 md:py-8">
        <div className="container max-w-4xl">
          <AdCarousel placement="browse-sidebar" />
        </div>
      </section>
    </div>
  )
}
